import { Injectable, inject } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { AuthTokenService } from "@firm360/libs/auth";
import {
    EMPTY,
    type Observable,
    catchError,
    defer,
    filter,
    map,
    merge,
    of,
    startWith,
    switchMap,
    tap,
} from "rxjs";
import { PendoProfileBuilder } from "./pendo-profile.builder";
import { PendoProfile } from "./pendo-profile.model";
import { IdentityProfileSource } from "./sources/identity/identity.pendo-profile-source";
import { LocalStorageProfileSource } from "./sources/local-storage/local-storage.pendo-profile-source";
import type { PendoProfileSource } from "./sources/pendo-profile-source";
import { EitherServerProfileSource } from "./sources/server/either-server.pendo-profile-source";

/**
 * The best profile of whoever the current session belongs to, from every
 * source that knows something about it.
 *
 * Every source starts at once and feeds a {@link PendoProfileBuilder}, so a
 * profile is available as soon as any source knows who this is — on a
 * reload, from the ids this device cached — and improves as the others
 * answer. A failure or a hang in one source only costs what it alone knows.
 *
 * Everything is keyed to the session token: a new token starts over, and
 * abandons whatever the previous session still had in flight.
 */
@Injectable()
export class PendoProfileService {
    private readonly _authTokenService = inject(AuthTokenService);
    private readonly _localStorage = inject(LocalStorageProfileSource);

    /** Most trusted first. */
    private readonly _sources: readonly PendoProfileSource[] = [
        inject(EitherServerProfileSource),
        inject(IdentityProfileSource),
        this._localStorage,
    ];

    /**
     * The profile each time it changes; `null` while no one is known — signed
     * out, or a session no source has answered for yet. Never errors.
     */
    readonly profile$: Observable<PendoProfile | null> = toObservable(
        this._authTokenService.token,
    ).pipe(
        switchMap((token) =>
            token ? this._profileFor$(token).pipe(startWith(null)) : of(null),
        ),
    );

    private _profileFor$(token: string): Observable<PendoProfile> {
        return defer(() => {
            const builder = new PendoProfileBuilder(this._sources);

            return merge(
                ...this._sources.map((source) =>
                    this._getProfile$(source, token).pipe(
                        map((answer) => builder.add(source, answer)),
                    ),
                ),
            ).pipe(
                filter((profile) => profile !== undefined),
                tap((profile) =>
                    this._localStorage.remember(
                        token,
                        PendoProfile.ids(profile),
                    ),
                ),
            );
        });
    }

    /** What a source answers, until it gives up. */
    private _getProfile$(
        source: PendoProfileSource,
        token: string,
    ): Observable<PendoProfile> {
        return defer(() => source.contribute$(token)).pipe(
            catchError((error: unknown) => {
                console.warn(
                    "A Pendo profile source gave up; the profile goes on without it:",
                    error,
                );
                return EMPTY;
            }),
        );
    }
}
